/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { BatchPostRequest } from '../models/BatchPostRequest';
import type { ChargeBack } from '../models/ChargeBack';
import type { FundingInfoUpdate } from '../models/FundingInfoUpdate';
import type { IvrTransactionCreate } from '../models/IvrTransactionCreate';
import type { PagedItems } from '../models/PagedItems';
import type { PagingQuery } from '../models/PagingQuery';
import type { TransactionCreate } from '../models/TransactionCreate';
import type { TransactionDetails } from '../models/TransactionDetails';
import type { TransactionDetailsMinimum } from '../models/TransactionDetailsMinimum';
import type { TransactionRefundCreateDTO } from '../models/TransactionRefundCreateDTO';
import type { TransactionVoidCreateDTO } from '../models/TransactionVoidCreateDTO';
import { request as __request } from '../core/request';

export class TransactionsVService {

    /**
     * @param billingAccountId
     * @param requestBody
     * @returns PagedItems OK
     * @throws ApiError
     */
    public static async transactionsVGetAll(
        billingAccountId: string,
        requestBody?: PagingQuery,
    ): Promise<PagedItems> {
        const result = await __request({
            method: 'POST',
            path: `/api/v2/billing_accounts/${billingAccountId}/transactions/get`,
            body: requestBody,
            errors: {
                400: `Bad Request`,
                401: `Unauthorized`,
            },
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @param requestBody
     * @returns TransactionDetails OK
     * @throws ApiError
     */
    public static async transactionsVGetExport(
        billingAccountId: string,
        requestBody?: PagingQuery,
    ): Promise<TransactionDetails> {
        const result = await __request({
            method: 'POST',
            path: `/api/v2/billing_accounts/${billingAccountId}/transactions/export`,
            body: requestBody,
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
            },
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @param requestBody
     * @returns TransactionDetailsMinimum OK
     * @throws ApiError
     */
    public static async transactionsVGetPdfExport(
        billingAccountId: string,
        requestBody?: PagingQuery,
    ): Promise<TransactionDetailsMinimum> {
        const result = await __request({
            method: 'POST',
            path: `/api/v2/billing_accounts/${billingAccountId}/transactions/export/pdf`,
            body: requestBody,
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
            },
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @param requestBody
     * @returns TransactionDetails Created
     * @throws ApiError
     */
    public static async transactionsVCreate(
        billingAccountId: string,
        requestBody?: TransactionCreate,
    ): Promise<TransactionDetails> {
        const result = await __request({
            method: 'POST',
            path: `/api/v2/billing_accounts/${billingAccountId}/transactions`,
            body: requestBody,
            errors: {
                400: `Bad Request`,
                401: `Unauthorized`,
                500: `Internal Server Error`,
            },
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @param transactionId
     * @returns TransactionDetails OK
     * @throws ApiError
     */
    public static async transactionsVGetById(
        billingAccountId: string,
        transactionId: string,
    ): Promise<TransactionDetails> {
        const result = await __request({
            method: 'GET',
            path: `/api/v2/billing_accounts/${billingAccountId}/transactions/${transactionId}`,
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
                404: `Not Found`,
            },
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @param transactionId
     * @param requestBody
     * @returns TransactionDetails OK
     * @throws ApiError
     */
    public static async transactionsVPostedDated(
        billingAccountId: string,
        transactionId: string,
        requestBody?: BatchPostRequest,
    ): Promise<TransactionDetails> {
        const result = await __request({
            method: 'PATCH',
            path: `/api/v2/billing_accounts/${billingAccountId}/transactions/${transactionId}`,
            body: requestBody,
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @param transactionId
     * @param requestBody
     * @returns TransactionDetails OK
     * @throws ApiError
     */
    public static async transactionsVVoid(
        billingAccountId: string,
        transactionId: string,
        requestBody?: TransactionVoidCreateDTO,
    ): Promise<TransactionDetails> {
        const result = await __request({
            method: 'POST',
            path: `/api/v2/billing_accounts/${billingAccountId}/transactions/${transactionId}/void`,
            body: requestBody,
            errors: {
                400: `Bad Request`,
                401: `Unauthorized`,
                403: `Forbidden`,
                500: `Internal Server Error`,
            },
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @param transactionId
     * @param requestBody
     * @returns TransactionDetails OK
     * @throws ApiError
     */
    public static async transactionsVRefund(
        billingAccountId: string,
        transactionId: string,
        requestBody?: TransactionRefundCreateDTO,
    ): Promise<TransactionDetails> {
        const result = await __request({
            method: 'POST',
            path: `/api/v2/billing_accounts/${billingAccountId}/transactions/${transactionId}/refund`,
            body: requestBody,
            errors: {
                400: `Bad Request`,
                401: `Unauthorized`,
                403: `Forbidden`,
                500: `Internal Server Error`,
            },
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @param requestBody
     * @returns TransactionDetails Created
     * @throws ApiError
     */
    public static async transactionsVPostIvrPayment(
        billingAccountId: string,
        requestBody?: IvrTransactionCreate,
    ): Promise<TransactionDetails> {
        const result = await __request({
            method: 'POST',
            path: `/api/v2/billing_accounts/${billingAccountId}/transactions/ivr`,
            body: requestBody,
            errors: {
                400: `Bad Request`,
                401: `Unauthorized`,
                500: `Internal Server Error`,
            },
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @param transactionId
     * @param transferToAccount
     * @returns TransactionDetails OK
     * @throws ApiError
     */
    public static async transactionsVTransferPayment(
        billingAccountId: string,
        transactionId: string,
        transferToAccount?: string,
    ): Promise<TransactionDetails> {
        const result = await __request({
            method: 'PATCH',
            path: `/api/v2/billing_accounts/${billingAccountId}/transactions/${transactionId}/transfer`,
            query: {
                'transferToAccount': transferToAccount,
            },
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @param transactionId
     * @param requestBody
     * @returns TransactionDetails OK
     * @throws ApiError
     */
    public static async transactionsVChargebackPayment(
        billingAccountId: string,
        transactionId: string,
        requestBody?: ChargeBack,
    ): Promise<TransactionDetails> {
        const result = await __request({
            method: 'PATCH',
            path: `/api/v2/billing_accounts/${billingAccountId}/transactions/${transactionId}/chargeback`,
            body: requestBody,
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @param requestBody
     * @returns any No Content
     * @throws ApiError
     */
    public static async transactionsVDeleteTransaction(
        billingAccountId: string,
        requestBody?: Array<string>,
    ): Promise<any> {
        const result = await __request({
            method: 'POST',
            path: `/api/v2/billing_accounts/${billingAccountId}/transactions/delete`,
            body: requestBody,
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @param requestBody
     * @returns any OK
     * @throws ApiError
     */
    public static async transactionsVDeleteTransaction1(
        billingAccountId: string,
        requestBody?: FundingInfoUpdate,
    ): Promise<any> {
        const result = await __request({
            method: 'PATCH',
            path: `/api/v2/billing_accounts/${billingAccountId}/transactions/funding-info`,
            body: requestBody,
        });
        return result.body;
    }

}